import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import OrganizationSchema from '@/components/base/OrganizationSchema';
import WebPageSchema from '@/components/base/WebPageSchema';
import FAQPageSchema from '@/components/base/FAQPageSchema';
import BreadcrumbListSchema from '@/components/base/BreadcrumbListSchema';

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message: string;
}

const stats = [
  { value: '2.4M', label: 'Monthly Readers' },
  { value: '500+', label: 'Articles Published Yearly' },
  { value: '140', label: 'Countries Reached' },
  { value: '12K+', label: 'Newsletter Subscribers' },
];

const team = [
  {
    name: 'Amara Osei',
    role: 'Editor-in-Chief',
    bio: 'Former investigative journalist at The Guardian with 15 years of experience in digital media.',
    imageSeq: 'team-01',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20editor%20in%20a%20warm%20lit%20modern%20newsroom%2C%20wearing%20a%20cream%20blazer%2C%20confident%20expression%2C%20soft%20natural%20lighting%2C%20editorial%20portrait%20photography%20style%20with%20clean%20background&width=400&height=500&seq=team-member-01&orientation=portrait',
  },
  {
    name: 'David Nakamura',
    role: 'Managing Editor',
    bio: 'Led digital transformation at three major publications before joining The Vertex in 2024.',
    imageSeq: 'team-02',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20male%20editor%20in%20a%20modern%20minimalist%20office%2C%20wearing%20a%20navy%20shirt%2C%20thoughtful%20expression%2C%20warm%20ambient%20lighting%2C%20editorial%20portrait%20photography%20with%20clean%20cream%20background&width=400&height=500&seq=team-member-02&orientation=portrait',
  },
  {
    name: 'Elena Vasquez',
    role: 'Head of Technology',
    bio: 'PhD in Computer Science from MIT. Leads our tech coverage and product innovation initiatives.',
    imageSeq: 'team-03',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20female%20tech%20leader%20in%20a%20bright%20modern%20workspace%2C%20wearing%20a%20white%20shirt%2C%20friendly%20confident%20expression%2C%20soft%20natural%20daylight%2C%20editorial%20portrait%20photography%20with%20clean%20background&width=400&height=500&seq=team-member-03&orientation=portrait',
  },
  {
    name: 'Marcus Chen',
    role: 'Creative Director',
    bio: 'Award-winning designer who shaped the visual identity of brands like Medium and Substack.',
    imageSeq: 'team-04',
    imageUrl: 'https://readdy.ai/api/search-image?query=Creative%20male%20director%20in%20a%20design%20studio%2C%20wearing%20a%20black%20turtleneck%2C%20artistic%20expression%2C%20warm%20studio%20lighting%2C%20editorial%20portrait%20photography%20with%20minimalist%20background&width=400&height=500&seq=team-member-04&orientation=portrait',
  },
];

const faqs = [
  {
    question: 'Is The Vertex free to read?',
    answer: 'Yes. The Vertex is completely free and will always remain that way. We believe great journalism should be accessible to everyone. Our operations are funded through voluntary reader contributions and select sponsorship partnerships that do not influence our editorial decisions.',
  },
  {
    question: 'How do you maintain editorial independence?',
    answer: 'We are independently owned with no corporate parent or private equity backing. Our funding comes from reader contributions and carefully vetted sponsorship partnerships. All editorial decisions are made by our newsroom team without input from funders, and we maintain a strict firewall between our business and editorial operations.',
  },
  {
    question: 'How can I contribute or submit a story?',
    answer: 'We welcome pitches from freelance journalists and subject matter experts. You can submit your story idea through our partnerships email at hello@cg91.cn. Please include a brief summary of your proposed piece, your relevant background, and any previous writing samples.',
  },
  {
    question: 'Do you accept guest posts or sponsored content?',
    answer: 'We do not publish traditional guest posts or sponsored content. However, we do collaborate with partner organizations on co-branded editorial projects where we maintain full editorial control. All such collaborations are clearly labeled for transparency.',
  },
  {
    question: 'How can my organization partner with The Vertex?',
    answer: 'We offer several partnership models including content syndication, editorial collaborations, and event sponsorships. Please fill out our business cooperation form on this page, and our partnerships team will reach out to discuss opportunities that align with our editorial mission.',
  },
  {
    question: 'How often do you publish new articles?',
    answer: 'We publish 10-15 original articles per week, including in-depth features, analysis pieces, and interviews. Our weekly newsletter digest goes out every Sunday morning to over 120,000 subscribers, featuring our best work from the past seven days.',
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<FormStatus>({ type: 'idle', message: '' });
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus({ type: 'success', message: 'Thank you! We will get back to you soon.' });
      form.reset();
      return;
    }

    formData.delete('company_alt');

    setFormStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch('http://cg91.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus({ type: 'success', message: 'Thank you! We will get back to you within 48 hours.' });
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || 'Something went wrong.';
        setFormStatus({ type: 'error', message: serverMsg });
      }
    } catch {
      setFormStatus({ type: 'error', message: 'Network error. Please try again later.' });
    }
  };

  return (
    <>
      <OrganizationSchema />
      <WebPageSchema
        url="http://cg91.cn/about"
        name="About The Vertex — Independent Journalism Platform"
        description="Learn about The Vertex — an independent, reader-supported publication delivering in-depth journalism at the intersection of technology, business, design, and culture. Meet our team and explore partnership opportunities."
        dateModified="2026-07-07"
      />
      <FAQPageSchema questions={faqs} />
      <BreadcrumbListSchema
        items={[
          { name: 'Home', url: 'http://cg91.cn/' },
          { name: 'About Us', url: 'http://cg91.cn/about' },
        ]}
      />
      <main className="min-h-screen bg-background-50 font-body">
        <Navbar />

        <section className="pt-24 md:pt-32 pb-14 md:pb-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <p className="text-xs font-label tracking-widest uppercase text-foreground-400 mb-4">
                About The Vertex
              </p>
              <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground-950 leading-tight mb-6">
                Independent journalism for the <span className="italic text-primary-600">curious</span> mind
              </h1>
              <p className="text-base md:text-lg text-foreground-500 leading-relaxed">
                Founded in 2022, The Vertex is a reader-supported publication dedicated to in-depth reporting at the intersection of technology, business, design, and culture. We believe that great journalism doesn&apos;t need paywalls — it needs passionate readers.
              </p>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="font-heading text-3xl md:text-5xl font-bold text-primary-600">{stat.value}</p>
                  <p className="mt-2 text-xs md:text-sm text-foreground-500 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-14">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">Our Story</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                  <p>
                    The Vertex was born from a simple conviction: that the best journalism deserves to be read by everyone. In an era of fragmented attention and algorithmic feeds, we set out to build a publication where depth and quality would always come first.
                  </p>
                  <p>
                    What started as a newsletter shared among 200 friends has grown into a global publication read by millions. Our team of 40+ journalists, editors, and designers spans 12 time zones, united by a shared commitment to stories that illuminate rather than inflame.
                  </p>
                  <p>
                    We are proudly independent. We have no corporate parent, no private equity backers, and no agenda beyond delivering the highest-quality journalism we can produce. Our funding comes from our readers — through voluntary memberships and community support — which means our only obligation is to you.
                  </p>
                </div>
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">Our Values</h2>
                <div className="space-y-5">
                  {[
                    { title: 'Accuracy above speed', desc: 'We would rather be second and right than first and wrong. Every fact is verified, every claim is sourced, and every story undergoes rigorous editorial review before publication.' },
                    { title: 'Depth without elitism', desc: 'Complex topics deserve nuanced treatment, but that does not mean they should be inaccessible. We write for the curious generalist, not the specialist.' },
                    { title: 'Independence as a principle', desc: 'We do not accept advertising that compromises editorial integrity. Our revenue model is built on reader support, ensuring our journalism serves you — not advertisers or shareholders.' },
                    { title: 'Diversity of perspective', desc: 'The best understanding emerges from the collision of different viewpoints. Our newsroom reflects the global audience we serve, with contributors from over 30 countries.' },
                  ].map((v) => (
                    <div key={v.title} className="flex gap-3">
                      <div className="flex-shrink-0 w-5 h-5 mt-0.5 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-check-line text-xs text-primary-600"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800 mb-1">{v.title}</h3>
                        <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10 md:mb-14">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">Meet the Team</h2>
              <p className="text-sm md:text-base text-foreground-500 max-w-xl">
                Our leadership team brings together decades of experience across journalism, technology, and design.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {team.map((member) => (
                <div key={member.name} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <div className="h-56 md:h-64 overflow-hidden">
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="w-full h-full object-cover object-top"
                      title={`${member.name} - ${member.role} at The Vertex`}
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">{member.name}</h3>
                    <p className="text-xs text-primary-600 font-label font-medium mb-2">{member.role}</p>
                    <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-3">Let&apos;s Work Together</h2>
                <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                  Interested in content partnerships, syndication opportunities, or collaborating on editorial projects? We would love to hear from you. Fill out the form and our partnerships team will get back to you within 48 hours.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-mail-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">Email</p>
                      <p className="text-sm text-foreground-700 font-medium">hello@cg91.cn</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">Office</p>
                      <p className="text-sm text-foreground-700 font-medium">244 Madison Avenue, New York, NY 10016</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  onSubmit={handleSubmit}
                 
                  id="business-cooperation-form"
                  className="bg-background-100 rounded-lg border border-background-200 p-5 md:p-6 space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="first_name" className="block text-xs font-medium text-foreground-600 mb-1.5">First Name</label>
                      <input
                        type="text"
                        id="first_name"
                        name="first_name"
                        required
                        className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="Your first name"
                      />
                    </div>
                    <div>
                      <label htmlFor="last_name" className="block text-xs font-medium text-foreground-600 mb-1.5">Last Name</label>
                      <input
                        type="text"
                        id="last_name"
                        name="last_name"
                        required
                        className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="Your last name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="hello@cg91.cn"
                    />
                  </div>

                  <div>
                    <label htmlFor="organization" className="block text-xs font-medium text-foreground-600 mb-1.5">Organization</label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      required
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                      placeholder="Your company or organization"
                    />
                  </div>

                  <div>
                    <label htmlFor="partnership_type" className="block text-xs font-medium text-foreground-600 mb-1.5">Partnership Type</label>
                    <select
                      id="partnership_type"
                      name="partnership_type"
                      required
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                    >
                      <option value="">Select type...</option>
                      <option value="content_partnership">Content Partnership</option>
                      <option value="syndication">Content Syndication</option>
                      <option value="sponsorship">Sponsorship</option>
                      <option value="editorial_collaboration">Editorial Collaboration</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-1.5">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="Tell us about your project or idea..."
                    ></textarea>
                    <p className="text-[10px] text-foreground-400 mt-1">Maximum 500 characters</p>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="form-honeypot"
                  />

                  <button
                    type="submit"
                    disabled={formStatus.type === 'loading'}
                    className="w-full py-3 rounded-md bg-primary-600 hover:bg-primary-700 text-background-50 text-sm font-label font-medium transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {formStatus.type === 'loading' ? (
                      <span>Sending...</span>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-send-plane-line"></i>
                      </>
                    )}
                  </button>

                  {formStatus.type === 'success' && (
                    <p className="text-sm text-primary-600 font-medium text-center">{formStatus.message}</p>
                  )}
                  {formStatus.type === 'error' && (
                    <p className="text-sm text-accent-600 font-medium text-center">{formStatus.message}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 text-center mb-3">
              Frequently Asked Questions
            </h2>
            <p className="text-sm md:text-base text-foreground-500 text-center mb-10 md:mb-12 max-w-lg mx-auto">
              Everything you need to know about The Vertex and how we operate.
            </p>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
                  >
                    <h4 className="text-sm md:text-base font-semibold text-foreground-800 pr-4">{faq.question}</h4>
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-200 ${openFaq === index ? 'rotate-180 bg-primary-100 text-primary-600' : 'bg-background-200 text-foreground-500'}`}>
                      <i className="ri-arrow-down-s-line"></i>
                    </div>
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}